// ----------------------------------------------------------------------------
// 📈 FRONTEND TELEMETRY (Azure Application Insights web SDK)
// Answers "what pages do users use, when, and which pages fail":
//   - Page views per route (enableAutoRouteTracking follows SPA navigation)
//   - Sessions/users, page visit time
//   - Unhandled JS exceptions + promise rejections (autoExceptionInstrumented)
//   - Failed fetch/XHR dependency calls
//   - Custom events (e.g. the chord upload funnel stages)
// No-op when VITE_APPINSIGHTS_CONNECTION_STRING is unset (local dev default),
// so nothing is collected unless an environment opts in. The connection string
// is not a secret (it ships in the client bundle by design).
// ----------------------------------------------------------------------------

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;

export function initTelemetry(): void {
  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as
    | string
    | undefined;
  if (!connectionString || appInsights) return;

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true, // SPA route changes become page views
        autoTrackPageVisitTime: true,
        enableUnhandledPromiseRejectionTracking: true,
        // Keep correlation headers OFF: the gateway's CORS policy does not
        // allow Request-Id/traceparent on cross-origin calls.
        enableCorsCorrelation: false,
        disableCookiesUsage: false,
      },
    });
    appInsights.loadAppInsights();
    appInsights.trackPageView();
  } catch {
    appInsights = null; // never let telemetry break the app
  }
}

/** Tag telemetry with the signed-in user + church so failures are attributable. */
export function setTelemetryUser(userId: string | null, churchId?: string | null): void {
  if (!appInsights) return;
  if (userId) {
    appInsights.setAuthenticatedUserContext(userId, churchId ?? undefined, true);
  } else {
    appInsights.clearAuthenticatedUserContext();
  }
}

/** Record a named product event (e.g. upload funnel stages). */
export function trackEvent(name: string, properties?: Record<string, unknown>): void {
  appInsights?.trackEvent({ name }, properties as { [key: string]: unknown } | undefined);
}

/** Record a handled error so it shows under Failures in App Insights. */
export function trackException(error: unknown, properties?: Record<string, unknown>): void {
  if (!appInsights) return;
  const exception = error instanceof Error ? error : new Error(String(error));
  appInsights.trackException(
    { exception },
    properties as { [key: string]: unknown } | undefined,
  );
}
